import Phaser from 'phaser';
import type { MassSystem } from '@/systems/MassSystem';
import type { BrainSystem } from '@/systems/BrainSystem';
import type { SentienceGate } from '@/systems/SentienceGate';
import type { AbilitySystem } from '@/systems/AbilitySystem';
import type { DnaSystem } from '@/systems/DnaSystem';
import { NarratorPanel } from './NarratorPanel';
import { StatusStrip } from './StatusStrip';
import { ActionBar } from './ActionBar';
import { GenomePanel } from './GenomePanel';

/**
 * Facade over the three HUD subsystems. The scene talks to HUD;
 * HUD dispatches to NarratorPanel, StatusStrip, ActionBar.
 */
export class HUD {
  readonly narrator: NarratorPanel;
  readonly status: StatusStrip;
  readonly actions: ActionBar;
  readonly genome: GenomePanel;
  private threatPulse: Phaser.GameObjects.Arc;

  constructor(
    scene: Phaser.Scene,
    mass: MassSystem,
    brain: BrainSystem,
    gate: SentienceGate,
    abilities: AbilitySystem,
    dna: DnaSystem,
  ) {
    this.narrator = new NarratorPanel(scene, gate);
    this.status = new StatusStrip(scene, mass, brain, gate, abilities);
    this.actions = new ActionBar(scene);
    this.genome = new GenomePanel(scene, dna);

    this.threatPulse = scene.add
      .circle(scene.scale.width - 32, 32, 16, 0xff4444, 0)
      .setScrollFactor(0, 0)
      .setDepth(1001);

    mass.onTierChange(() => this.status.refresh());
    brain.onLevelChange(() => this.status.refresh());
    gate.onLevelChange(() => this.status.refresh());
    abilities.onUnlock((ability) => this.narrator.show(`[new: ${ability}]`, 3000));
  }

  refresh(): void {
    this.status.refresh();
  }

  showNarrator(text: string, durationMs = 4500): void {
    this.narrator.show(text, durationMs);
  }

  pulseTier(): void {
    this.status.pulseTier();
  }

  pulseThreat(intensity = 1): void {
    this.threatPulse.setFillStyle(0xff4444, Math.min(1, intensity));
    this.threatPulse.scene.tweens.add({
      targets: this.threatPulse,
      scale: { from: 1, to: 1.8 },
      alpha: { from: intensity, to: 0 },
      duration: 500,
    });
  }

  destroy(): void {
    this.narrator.destroy();
    this.status.destroy();
    this.actions.destroy();
    this.genome.destroy();
    this.threatPulse.destroy();
  }
}
